import { Injectable, inject } from '@angular/core';

import { BetBuilderFixture } from '@cds/betting-offer/domain-specific/bet-builder';
import { VirtualCompetition, VirtualCompetitionGroup } from '@cds/betting-offer/tags';
import { StatisticsMode } from '@cds/query-objects';
import { BetBuilderMapperService, DetailedFixtureFactory, DetailedGroupingFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import {
    EnhancedFixtureViewGroupingConfiguration,
    EventModel,
    EventOptionGroup,
    EventOptionGroupType,
    EventOptionSet,
    EventOptionSetHeaderTab,
    ExtendedDisplayType,
    MyMarketModel,
    OptionGroup,
    PRECREATED_BAB_EMPTY_THEME_ID,
    ParticipantInfo,
    SetTab,
    getPitcherByParticipantType,
    getRankByParticipantType,
    getWinLossStatsByParticipantType,
} from '@frontend/sports/betting-offer/feature/model';
import { BettingOfferService } from '@frontend/sports/betting-offer/feature/offer-service';
import { BetBuilderConfig, BettingInsightsConfiguration } from '@frontend/sports/common/client-config-data-access';
import { StatisticsConfigService } from '@frontend/sports/statistics/core/feature';
import { LocalStoreService, NativeAppService } from '@frontend/vanilla/core';
import { includes, isEmpty, maxBy, sumBy, uniq, uniqBy } from 'lodash-es';

import { PrepareOptionGroupsParams, ResultEventModel, VirtualCompetitionDetails } from './event-details-common.models';

@Injectable({ providedIn: 'root' })
export class EventDetailsService {
    private bettingOffer = inject(BettingOfferService);
    private fixtureFactory = inject(DetailedFixtureFactory);
    private groupingFactory = inject(DetailedGroupingFactory);
    private betBuilderMapper = inject(BetBuilderMapperService);
    private betBuilderConfig = inject(BetBuilderConfig);
    private bettingInsightsConfig = inject(BettingInsightsConfiguration);
    statisticsConfigService = inject(StatisticsConfigService);
    private localStore = inject(LocalStoreService);
    private nativeAppService = inject(NativeAppService);

    private storageKey = 'sia_my_markets';
    participantInfos: ParticipantInfo[];
    teamRecordsEnabled = false;
    teamRanksEnabled = false;
    teamPitchersEnabled = false;

    async getEvent(
        id: string,
        excludeMarkets: boolean = false,
        includeRelatedFixtures?: boolean,
        marketGroupIds?: string,
    ): Promise<EventModel | undefined> {
        const cdsEntity = await this.bettingOffer.getFixtureViewModel({
            id,
            excludeMarkets,
            statsMode: StatisticsMode.None,
            isBettingInsightsEnabled: this.bettingInsightsConfig.isEnabled,
            includeRelatedFixtures,
            marketGroupIds,
        });
        if (!cdsEntity) {
            return;
        }

        return this.fixtureFactory.create({
            fixture: cdsEntity.fixture,
            splitFixtures: cdsEntity.splitFixtures,
            grouping: cdsEntity.grouping,
            loadEventSitemap: true,
            bettingInsights: cdsEntity.bettingInsights,
        });
    }

    async getModel(id: string): Promise<ResultEventModel | undefined> {
        const statsMode = this.statisticsConfigService.getStatisticsModesBySportId();
        const cdsEntity = await this.bettingOffer.getFixtureViewModel({
            id,
            statsMode,
            includePrecreated: this.betBuilderConfig.isPrecreatedBetBuilderEnabled,
            isBettingInsightsEnabled: this.bettingInsightsConfig.isEnabled,
        });
        if (!cdsEntity) {
            return;
        }

        this.addBetbuilderToGrouping(cdsEntity.grouping, cdsEntity.preCreatedOffer);

        return {
            model: this.fixtureFactory.create({
                fixture: cdsEntity.fixture,
                splitFixtures: cdsEntity.splitFixtures,
                grouping: cdsEntity.grouping,
                precreated: cdsEntity.preCreatedOffer,
                loadEventSitemap: true,
                bettingInsights: cdsEntity.bettingInsights,
                excludePriceboostedMarketGrouping: !this.nativeAppService.isTerminal,
            }),
            preCreated: this.betBuilderMapper.mapFixture(cdsEntity.preCreatedOffer),
            virtualCompetitionDetails: this.mapVirtualCompetitionDetails(
                cdsEntity.fixture.virtualCompetition,
                cdsEntity.fixture.virtualCompetitionGroup,
            ),
            fixture: cdsEntity.fixture,
            betBuilderFixture: cdsEntity.preCreatedOffer,
        };
    }

    private mapVirtualCompetitionDetails(
        virtualCompetition?: VirtualCompetition,
        virtualCompetitionGroup?: VirtualCompetitionGroup,
    ): VirtualCompetitionDetails | undefined {
        if (!virtualCompetition) {
            return;
        }

        return {
            id: virtualCompetition.id,
            name: virtualCompetition.name.value,
            group: virtualCompetitionGroup
                ? {
                      id: virtualCompetitionGroup.id,
                      name: virtualCompetitionGroup.name.value,
                  }
                : undefined,
        };
    }

    addBetbuilderToGrouping(
        grouping: EnhancedFixtureViewGroupingConfiguration,
        preCreatedOffer?: BetBuilderFixture,
    ): EnhancedFixtureViewGroupingConfiguration {
        const themedTabs = uniq(preCreatedOffer?.precreatedMarketsInfo.map((x) => x.themedTab?.toUpperCase()));
        const sportSpecificThemedTabs = this.betBuilderConfig.themedTabs[grouping.sportId] || [];
        const themedTabNames = sportSpecificThemedTabs.filter((i) => includes(themedTabs, i.key?.toUpperCase().trim())).map((s) => s.name);
        const marketTabId = maxBy(grouping.marketTabs.map((x) => x.id));
        if (marketTabId) {
            let count = marketTabId + 1;
            if (themedTabNames?.length) {
                themedTabNames.forEach((tabs) => {
                    grouping.marketTabs.push({ id: count, name: tabs || '', sortOrder: count });
                    count++;
                });
            }

            grouping.marketTabs.push({ id: count, name: '', sortOrder: count });
        }

        return grouping;
    }

    prepareEventOptionGroups(params: PrepareOptionGroupsParams): EventOptionGroup[] {
        const { event, groupedSetsIds, tag, setTags, isSgp } = params;
        const activeTab = params.activeTab ?? SetTab.All;
        let optionGroups = [];

        if (activeTab === SetTab.MyMarkets) {
            optionGroups = this.getOptionGroupForMyMarkets(event);
        } else if (groupedSetsIds && groupedSetsIds.length > 0) {
            optionGroups = this.getMultipleOptionGroups(event.optionGroups, groupedSetsIds);

            if (setTags && setTags.length >= 1) {
                optionGroups = optionGroups.filter(
                    (group) =>
                        group.detailedGrouping?.tags &&
                        group.detailedGrouping.tags.length >= setTags.length &&
                        setTags.every((t, index) => t === group.detailedGrouping.tags![index].key) &&
                        (isSgp ? group.isBetBuilder : true),
                );
            }
        } else {
            optionGroups =
                activeTab === SetTab.All
                    ? event.optionGroups
                    : event.optionGroups.filter((group) => group.detailedGrouping && group.detailedGrouping.marketSet === activeTab);

            if (tag) {
                optionGroups = optionGroups.filter((group) => group.detailedGrouping?.tags?.length && group.detailedGrouping.tags[0].key === tag);
            }

            if (isSgp) {
                optionGroups = optionGroups.filter((group) => group.isBetBuilder);
            }
        }

        optionGroups = this.distinctOptionGroups(optionGroups);

        if (!params.isPrecreatedBABTab) {
            optionGroups = this.filterBetBuilderThemedOptionGroups(optionGroups);
        }

        if (params.isPrecreatedBABTab) {
            if (params.event.precreatedOptionGroups?.length) {
                return this.groupingFactory.transformEventGroupsPerPrecreatedGroup(optionGroups, params.event.precreatedOptionGroups);
            }

            return this.groupingFactory.groupMarkets(optionGroups, true, true);
        }

        return this.groupingFactory.groupMarkets(optionGroups);
    }

    private filterBetBuilderThemedOptionGroups(optionGroups: EventOptionGroup[]): EventOptionGroup[] {
        const isThemeTabAssigned = optionGroups.some(
            (og) =>
                og.detailedGrouping.displayType === ExtendedDisplayType.BetBuilder &&
                og.detailedGrouping.marketTabId !== PRECREATED_BAB_EMPTY_THEME_ID,
        );
        if (isThemeTabAssigned)
            return optionGroups.filter((optionGroup) => optionGroup.detailedGrouping.marketTabId !== PRECREATED_BAB_EMPTY_THEME_ID);

        return optionGroups;
    }

    isPreCreatedBABTab(event: EventModel, activeTab: string | SetTab = SetTab.All): boolean {
        return event.optionSets.find((t) => t.id === Number(activeTab))?.isPrecreated!;
    }

    getOptionGroupForMyMarkets(event: EventModel): EventOptionGroup[] {
        const myMarkets = this.localStore.get(this.storageKey) as MyMarketModel;
        if ((event.live && myMarkets?.live) || (!event.live && myMarkets?.prematch)) {
            //@ts-ignore allow dynamic accessor property, please don't use this, instead, use typing.
            const sportMarkets = event.live ? myMarkets.live[event.sport.id] : myMarkets.prematch[event.sport.id];

            if (sportMarkets) {
                return event.optionGroups.filter((group) => {
                    if (group.detailedGrouping) {
                        const market = sportMarkets.find(
                            (sMarket: OptionGroup) =>
                                sMarket.market_group_id === group.detailedGrouping.marketSet &&
                                sMarket.tv1_market_group_item === group.detailedGrouping.marketOrder,
                        );
                        if (market) {
                            group.position = market.position;

                            return true;
                        }

                        return false;
                    }

                    return false;
                });
            }
        }

        return [];
    }

    prepareEventOptionSetHeaderTab(set: EventOptionSet, optionGroups: EventOptionGroup[]): EventOptionSetHeaderTab {
        return <EventOptionSetHeaderTab>{
            id: set.id,
            name: set.name,
            count: optionGroups.length,
            optionGroups,
        };
    }

    calculateEventOptionGroupsCount(event: EventModel): number {
        const optionGroups = this.prepareEventOptionGroups({ event });

        return sumBy(optionGroups, (value) => value.getSubGroups(EventOptionGroupType.Period).length || 1);
    }

    private distinctOptionGroups(optionGroups: EventOptionGroup[]): EventOptionGroup[] {
        return uniqBy(
            optionGroups,
            (item) =>
                `${item.id}-${item.templateId}-${item.detailedGrouping.displayType}${item.detailedGrouping.sixPackGridId ? '-' + item.detailedGrouping.sixPackGridId : ''}`,
        );
    }

    private getMultipleOptionGroups(optionGroups: EventOptionGroup[], groupIds: number[]): EventOptionGroup[] {
        const groups: EventOptionGroup[] = [];
        groupIds.forEach((g) => {
            groups.push(...optionGroups.filter((group) => group.detailedGrouping && group.detailedGrouping.marketSet === g));
        });

        return groups;
    }

    setParticipantInfos(event: EventModel): ParticipantInfo[] {
        this.teamPitchersEnabled = !!this.statisticsConfigService.pitcherEnabledSports[event.sport.id];
        this.teamRecordsEnabled = !isEmpty(this.statisticsConfigService.teamRecordsFormat[event.sport.id]);
        this.teamRanksEnabled = !!this.statisticsConfigService.teamRankEnabledSports[event.sport.id];

        return (this.participantInfos = event.participants.map((participant) => {
            const rank = this.teamRanksEnabled ? getRankByParticipantType(event, participant.type) : undefined;
            const pitcher = !event.scoreboard.started && this.teamPitchersEnabled ? getPitcherByParticipantType(event, participant.type) : undefined;
            const teamRecordsFormat = this.statisticsConfigService.teamRecordsFormat[event.sport.id];
            const winLossStats =
                !event.scoreboard.started && teamRecordsFormat && this.teamRecordsEnabled
                    ? getWinLossStatsByParticipantType(event, participant.type!, teamRecordsFormat)
                    : undefined;

            return {
                participant,
                rank,
                winLossStats,
                pitcher,
            } as ParticipantInfo;
        }));
    }
}
